<template>
  <br/>
  <div class="row">
    <div class="col-12 mb-3">
      <h4>Returned creators</h4>
      <p class="text-muted mb-0">
        Creators who published again after at least six months without a published post.
      </p>
    </div>

    <div v-if="successMessage" class="col-12">
      <div class="alert alert-success" role="alert">
        {{ successMessage }}
      </div>
    </div>
    <div v-if="errorMessage" class="col-12">
      <div class="alert alert-danger" role="alert">
        {{ errorMessage }}
      </div>
    </div>
  </div>

  <form class="row" @submit.prevent="applyFilters">
    <div class="col-xxl-3 col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-3">
      <label for="returned-creators-search" class="form-label">Creator or channel</label>
      <input
        id="returned-creators-search"
        v-model="filters.keyword"
        class="form-control"
        type="search"
        placeholder="Name, slug, email or description"
      />
    </div>

    <div class="col-xxl-3 col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-3">
      <label for="returned-creators-country" class="form-label">Creator country</label>
      <select id="returned-creators-country" v-model="filters.country" class="form-control">
        <option value="">All</option>
        <option v-for="country in countries" :key="country.value" :value="country.value">
          {{ country.label || country.name || country.value }}
        </option>
      </select>
    </div>

    <div class="col-xxl-3 col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-3">
      <label for="returned-creators-status" class="form-label">Creator status</label>
      <select id="returned-creators-status" v-model="filters.creatorStatus" class="form-control">
        <option value="">All</option>
        <option v-for="status in creatorStatuses" :key="status.value" :value="status.value">
          {{ status.name }}
        </option>
      </select>
    </div>

    <div class="col-xxl-3 col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-3">
      <label for="returned-creators-category" class="form-label">Creator category</label>
      <select id="returned-creators-category" v-model="filters.categoryId" class="form-control">
        <option value="">All</option>
        <option v-for="category in contentCategories" :key="category.id" :value="category.id">
          {{ category.name }}
        </option>
      </select>
    </div>

    <div class="col-xxl-3 col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-3">
      <label for="returned-creators-language" class="form-label">Content language</label>
      <select id="returned-creators-language" v-model="filters.contentLanguage" class="form-control">
        <option value="">All</option>
        <option v-for="language in contentLanguages" :key="language.value" :value="language.value">
          {{ language.label }}
        </option>
      </select>
    </div>

    <div class="col-xxl-3 col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-3">
      <label for="returned-creators-inactivity" class="form-label">Inactivity duration</label>
      <select id="returned-creators-inactivity" v-model="filters.inactivityPeriod" class="form-control">
        <option value="">All</option>
        <option v-for="option in inactivityPeriods" :key="option.value" :value="option.value">
          {{ option.label }}
        </option>
      </select>
    </div>

    <div class="col-xxl-3 col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-3">
      <label for="returned-creators-monetization" class="form-label">Monetization status</label>
      <select id="returned-creators-monetization" v-model="filters.monetizationStatus" class="form-control">
        <option value="">All</option>
        <option v-for="option in monetizationStatuses" :key="option.value" :value="option.value">
          {{ option.label }}
        </option>
      </select>
    </div>

    <div class="col-xxl-3 col-xl-4 col-lg-6 col-md-6 col-sm-12 mb-3">
      <label for="returned-creators-per-page" class="form-label">Per page</label>
      <select id="returned-creators-per-page" v-model="perPage" class="form-control" @change="changePerPage">
        <option :value="10">10</option>
        <option :value="20">20</option>
        <option :value="50">50</option>
        <option :value="100">100</option>
      </select>
    </div>

    <div class="col-12 mb-4 d-flex gap-2">
      <button type="submit" class="btn btn-primary" :disabled="loading">
        Apply filters
      </button>
      <button type="button" class="btn btn-outline-secondary" :disabled="loading" @click="clearFilters">
        Clear filters
      </button>
      <button
        type="button"
        class="btn btn-success text-white"
        aria-label="Export returned creators as CSV"
        :disabled="loading || exporting"
        @click="exportReturnedCreators"
      >
        <span v-if="!exporting">Export CSV</span>
        <font-awesome-icon v-else icon="fa-solid fa-sync" spin/>
      </button>
    </div>
  </form>

  <div class="table-responsive">
    <table class="table table-hover align-middle returned-creators-table">
      <thead>
      <tr>
        <th scope="col">Creator</th>
        <th scope="col">Channel</th>
        <th scope="col">Return / inactivity</th>
        <th scope="col">Country / language</th>
        <th scope="col">Creator category</th>
        <th scope="col">Monetization status</th>
        <th scope="col" class="returned-creators-action-cell">Action</th>
      </tr>
      </thead>
      <tbody>
      <tr v-if="loading">
        <td colspan="7" class="text-center">
          <font-awesome-icon icon="fa-solid fa-sync" spin/>
        </td>
      </tr>
      <tr v-else-if="returnedCreators.length > 0" v-for="creator in returnedCreators" :key="creator.id">
        <td>
          <router-link
            v-if="creator.user_id"
            :to="{name: 'SingleUser', params: {id: creator.user_id}}"
            class="d-block"
          >
            {{ valueOrPlaceholder(creator.creator_name) }}
          </router-link>
          <span v-else class="d-block">{{ valueOrPlaceholder(creator.creator_name) }}</span>
          <a v-if="isSafeEmail(creator.creator_email)" :href="`mailto:${creator.creator_email}`">
            {{ creator.creator_email }}
          </a>
          <span v-else>{{ valueOrPlaceholder(creator.creator_email) }}</span>
        </td>
        <td class="returned-creators-channel">
          <div class="d-flex flex-column align-items-start gap-1">
            <a
              v-if="isSafeUrl(creator.channel_link)"
              :href="creator.channel_link"
              target="_blank"
              rel="noopener noreferrer"
            >
              {{ valueOrPlaceholder(creator.channel_name) }}
            </a>
            <span v-else>{{ valueOrPlaceholder(creator.channel_name) }}</span>
            <span :class="channelStatusBadgeClass(creator.creator_status)">
              {{ valueOrPlaceholder(creator.creator_status_label) }}
            </span>
          </div>
        </td>
        <td>
          <div class="d-flex flex-column align-items-start gap-1">
            <span class="text-nowrap">{{ formatDateTime(creator.returned_at) }}</span>
            <span class="badge badge-info">{{ valueOrPlaceholder(creator.inactivity_period_label) }}</span>
          </div>
        </td>
        <td>
          <div class="d-flex flex-column gap-1">
            <span>Country: {{ valueOrPlaceholder(creator.creator_country) }}</span>
            <span>Language: {{ formatLanguage(creator.content_language) }}</span>
          </div>
        </td>
        <td>{{ valueOrPlaceholder(creator.creator_category) }}</td>
        <td>
          <span :class="monetizationBadgeClass(creator.monetization_status)">
            {{ valueOrPlaceholder(creator.monetization_status_label) }}
          </span>
        </td>
        <td class="returned-creators-action-cell">
          <button
            type="button"
            class="btn btn-outline-secondary btn-sm returned-creators-hide-button"
            :disabled="hidingCreatorId === creator.id"
            @click="hideReturnedCreator(creator)"
          >
            <span v-if="hidingCreatorId !== creator.id">Hide</span>
            <font-awesome-icon v-else icon="fa-solid fa-sync" spin/>
          </button>
        </td>
      </tr>
      <tr v-else>
        <td colspan="7" class="text-center">No returned creators found</td>
      </tr>
      </tbody>
    </table>
  </div>

  <div v-if="totalRecords >= 1" class="d-flex flex-wrap justify-content-between align-items-center gap-3">
    <p class="mb-0">Total returned creators: {{ totalRecords }}</p>
    <nav v-if="totalPages >= 1" aria-label="Returned creators pagination">
      <ul class="pagination mb-0">
        <li class="page-item" :class="{disabled: currentPage <= 1}">
          <button
            type="button"
            class="page-link"
            :disabled="currentPage <= 1"
            @click="paginate(currentPage - 1)"
          >
            Previous
          </button>
        </li>
        <li class="page-item disabled">
          <span class="page-link">Page {{ currentPage }} of {{ totalPages }}</span>
        </li>
        <li class="page-item" :class="{disabled: currentPage >= totalPages}">
          <button
            type="button"
            class="page-link"
            :disabled="currentPage >= totalPages"
            @click="paginate(currentPage + 1)"
          >
            Next
          </button>
        </li>
      </ul>
    </nav>
  </div>
</template>

<script>
import axios from 'axios';
import Swal from 'sweetalert2';

function emptyFilters() {
  return {
    keyword: '',
    inactivityPeriod: '',
    country: '',
    categoryId: '',
    contentLanguage: '',
    creatorStatus: '',
    monetizationStatus: '',
  };
}

export default {
  data() {
    return {
      loading: true,
      returnedCreators: [],
      filters: emptyFilters(),
      activeFilters: emptyFilters(),
      countries: [],
      contentCategories: [],
      contentLanguages: [],
      creatorStatuses: [],
      inactivityPeriods: [
        {value: '6_to_9_months', label: '6–9 months'},
        {value: '9_to_12_months', label: '9–12 months'},
        {value: '12_to_24_months', label: '12–24 months'},
        {value: '24_plus_months', label: '24+ months'},
      ],
      monetizationStatuses: [
        {value: 'has_active_subscriptions', label: 'Has active subscriptions'},
        {value: 'has_subscribers', label: 'Has subscribers'},
        {value: 'has_other_active_monetization', label: 'Has other active monetization'},
        {value: 'no_active_monetization', label: 'No active monetization'},
      ],
      currentPage: 1,
      totalPages: 0,
      totalRecords: 0,
      perPage: 20,
      hidingCreatorId: null,
      exporting: false,
      successMessage: '',
      errorMessage: '',
      requestId: 0,
    };
  },

  mounted() {
    this.loadFilterOptions();
    this.fetchReturnedCreators();
  },

  methods: {
    async loadFilterOptions() {
      const requests = await Promise.allSettled([
        axios.get('/admin/countries'),
        axios.get('/admin/content-categories', {params: {per_page: 100}}),
        axios.get('/admin/languages'),
        axios.get('/admin/channel/statuses'),
      ]);

      const countriesResponse = requests[0];
      if (countriesResponse.status === 'fulfilled') {
        const countries = countriesResponse.value.data;
        this.countries = Array.isArray(countries) ? countries : [];
      }

      const categoriesResponse = requests[1];
      if (categoriesResponse.status === 'fulfilled') {
        const payload = categoriesResponse.value.data;
        this.contentCategories = Array.isArray(payload) ? payload : (payload.data || []);
      }

      const languagesResponse = requests[2];
      if (languagesResponse.status === 'fulfilled') {
        const languages = languagesResponse.value.data || {};
        this.contentLanguages = Object.entries(languages).map(([value, language]) => ({
          value,
          label: language.name || language.native || value,
        }));
      }

      const statusesResponse = requests[3];
      if (statusesResponse.status === 'fulfilled') {
        const statuses = statusesResponse.value.data && statusesResponse.value.data.status;
        this.creatorStatuses = Array.isArray(statuses) ? statuses : [];
      }
    },

    buildFilterParams() {
      const params = {};
      const filterMap = {
        keyword: 'keyword',
        inactivityPeriod: 'inactivity_period',
        country: 'country',
        categoryId: 'category_id',
        contentLanguage: 'content_language',
        creatorStatus: 'creator_status',
        monetizationStatus: 'monetization_status',
      };

      Object.entries(filterMap).forEach(([localName, apiName]) => {
        if (this.activeFilters[localName] !== '') {
          params[apiName] = this.activeFilters[localName];
        }
      });

      return params;
    },

    buildListParams(page) {
      return {
        ...this.buildFilterParams(),
        page,
        per_page: this.perPage,
      };
    },

    async fetchReturnedCreators(page = this.currentPage) {
      const currentRequestId = ++this.requestId;
      this.loading = true;
      this.errorMessage = '';

      try {
        const response = await axios.get('/admin/returned-channels', {
          params: this.buildListParams(page),
        });

        if (currentRequestId !== this.requestId) {
          return;
        }

        const payload = response.data || {};
        const totalPages = Number(payload.totalPages || 0);

        if (page > totalPages && totalPages >= 1) {
          this.currentPage = totalPages;
          await this.fetchReturnedCreators(totalPages);
          return;
        }

        this.returnedCreators = Array.isArray(payload.data) ? payload.data : [];
        this.currentPage = Number(payload.pageNumber || page);
        this.totalPages = totalPages;
        this.totalRecords = Number(payload.totalRecords || 0);
      } catch (error) {
        if (currentRequestId !== this.requestId) {
          return;
        }

        this.returnedCreators = [];
        this.totalPages = 0;
        this.totalRecords = 0;
        this.errorMessage = this.responseErrorMessage(error, 'Failed to load returned creators.');
      } finally {
        if (currentRequestId === this.requestId) {
          this.loading = false;
        }
      }
    },

    applyFilters() {
      this.activeFilters = {...this.filters};
      this.errorMessage = '';
      this.currentPage = 1;
      this.fetchReturnedCreators(1);
    },

    clearFilters() {
      this.filters = emptyFilters();
      this.activeFilters = emptyFilters();
      this.currentPage = 1;
      this.errorMessage = '';
      this.fetchReturnedCreators(1);
    },

    changePerPage() {
      this.currentPage = 1;
      this.fetchReturnedCreators(1);
    },

    paginate(page) {
      if (page < 1 || page > this.totalPages || page === this.currentPage) {
        return;
      }

      this.currentPage = page;
      this.fetchReturnedCreators(page);
    },

    async exportReturnedCreators() {
      this.exporting = true;
      this.errorMessage = '';

      try {
        const response = await axios.get('/admin/export/returned-channels', {
          params: this.buildFilterParams(),
          responseType: 'blob',
        });
        const blob = new Blob([response.data], {type: 'text/csv;charset=utf-8'});
        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const timestamp = new Date().toISOString()
          .replace(/[:.]/g, '_')
          .replace('T', '_')
          .replace('Z', '');

        link.href = objectUrl;
        link.download = `returned_creators_${timestamp}.csv`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(objectUrl);

        this.$toast.success('Returned creators exported successfully!');
      } catch (error) {
        this.errorMessage = this.responseErrorMessage(error, 'Failed to export returned creators.');
        this.$toast.error(this.errorMessage);
      } finally {
        this.exporting = false;
      }
    },

    async hideReturnedCreator(creator) {
      const confirmation = await Swal.fire({
        title: 'Hide returned creator?',
        text: `${creator.creator_name || 'This creator'} will be hidden from this report only.`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Hide',
        cancelButtonText: 'Cancel',
      });

      if (!confirmation.isConfirmed) {
        return;
      }

      this.hidingCreatorId = creator.id;
      this.successMessage = '';
      this.errorMessage = '';

      try {
        await axios.delete(`/admin/returned-channels/${creator.id}`);
        this.successMessage = 'Returned creator was hidden successfully.';

        const targetPage = this.returnedCreators.length === 1 && this.currentPage > 1
          ? this.currentPage - 1
          : this.currentPage;

        await this.fetchReturnedCreators(targetPage);
      } catch (error) {
        this.errorMessage = this.responseErrorMessage(error, 'Failed to hide the returned creator.');
      } finally {
        this.hidingCreatorId = null;
      }
    },

    responseErrorMessage(error, fallback) {
      if (error.response && error.response.data && error.response.data.message) {
        return error.response.data.message;
      }

      return fallback;
    },

    isSafeUrl(url) {
      if (!url || typeof url !== 'string') {
        return false;
      }

      try {
        const {protocol} = new URL(url);
        return protocol === 'http:' || protocol === 'https:';
      } catch {
        return false;
      }
    },

    isSafeEmail(email) {
      if (!email || typeof email !== 'string') {
        return false;
      }

      return /^[^\s@"'<>]+@[^\s@"'<>]+\.[^\s@"'<>]+$/.test(email);
    },

    valueOrPlaceholder(value) {
      return value !== null && value !== undefined && value !== '' ? value : '-';
    },

    formatDateTime(value) {
      if (!value) {
        return '-';
      }

      const date = new Date(String(value).replace(' ', 'T'));

      if (Number.isNaN(date.getTime())) {
        return value;
      }

      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');

      return `${year}-${month}-${day} ${hours}:${minutes}`;
    },

    formatLanguage(languageCode) {
      if (!languageCode) {
        return '-';
      }

      const language = this.contentLanguages.find(option => option.value === languageCode);
      return language ? `${language.label} (${languageCode})` : languageCode;
    },

    channelStatusBadgeClass(status) {
      const classes = {
        OK: 'badge badge-success',
        denied: 'badge badge-danger',
        paused: 'badge badge-warning',
        awaiting_confirmation: 'badge badge-info',
        pending_check: 'badge badge-info',
        stripe_confirmed_not_published: 'badge badge-info',
        ipay_confirmed_not_published: 'badge badge-info',
      };

      return classes[status] || 'badge badge-secondary';
    },

    monetizationBadgeClass(status) {
      const classes = {
        has_active_subscriptions: 'badge badge-success',
        has_subscribers: 'badge badge-primary',
        has_other_active_monetization: 'badge badge-info',
        no_active_monetization: 'badge badge-secondary',
      };

      return classes[status] || 'badge badge-secondary';
    },
  },
};
</script>

<style scoped>
.returned-creators-table {
  min-width: 82rem;
}

.returned-creators-channel {
  min-width: 10rem;
}

.returned-creators-action-cell {
  position: sticky;
  right: 0;
  z-index: 2;
  min-width: 5rem;
  background-color: var(--bs-body-bg, #fff);
  box-shadow: -6px 0 12px -6px rgba(0, 0, 0, 0.15);
}

.returned-creators-table thead .returned-creators-action-cell {
  z-index: 3;
}

.returned-creators-hide-button {
  padding: 0.15rem 0.4rem;
  font-size: 0.7rem;
  line-height: 1.2;
}
</style>
